// File: src/Dashboard.js
import React, { useEffect, useState, useCallback } from "react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, PieChart, Pie, Cell
} from "recharts";
import API from "./config";
import "./App.css";

// --- Theme Colors ---
const COLORS = ["#0088FE", "#00C49F", "#FFBB28", "#FF8042", "#8884d8", "#82ca9d"];
const THEME_BLUE = "#094978";

/* ==========================================================================
   SECTION 1: DATA HELPERS & NETWORK UTILS
   ========================================================================== */

const fetchJSON = (url, opts = {}) =>
  fetch(url, opts).then(async (r) => {
    const txt = await r.text();
    try {
      return { ok: r.ok, status: r.status, data: JSON.parse(txt) };
    } catch {
      return { ok: r.ok, status: r.status, data: txt };
    }
  });

const getSafeVal = (row, key, defaultVal = "Unknown") => {
  return row[key] ? String(row[key]).trim() : defaultVal;
};

const getSafeAmount = (row) => {
  let val = row["AMT_PAID"];
  if (val === undefined || val === null || val === "") return 0;
  val = String(val).replace(/,/g, "");
  return parseFloat(val) || 0;
};

const formatCurrency = (num) => {
  if (num === undefined || num === null || isNaN(num)) return "0";
  return num.toLocaleString("en-IN", {
    maximumFractionDigits: 0,
    style: 'currency',
    currency: 'INR'
  });
};

/* ==========================================================================
   SECTION 2: MAIN COMPONENT
   ========================================================================== */

export default function Dashboard({ token }) {
  const [loading, setLoading] = useState(true);
  const [loadingPercent, setLoadingPercent] = useState(0);

  // Expanded Stats State
  const [stats, setStats] = useState({
    totalBeneficiaries: 0,
    totalDisbursed: 0,
    avgDisbursal: 0,
    totalStreams: 0,
    maleBeneficiaries: 0,
    femaleBeneficiaries: 0,
    maleFund: 0,
    femaleFund: 0
  });

  // State for the dynamic start year
  const [startYear, setStartYear] = useState("2022");

  const [trendData, setTrendData] = useState([]);      
  const [genderData, setGenderData] = useState([]);    

  useEffect(() => {
    let timer;
    if (loading) {
      setLoadingPercent(0);
      timer = setInterval(() => {
        setLoadingPercent((prev) => (prev >= 95 ? prev : prev + 5));
      }, 100);
    } else {
      setLoadingPercent(100);
      setTimeout(() => setLoadingPercent(0), 500);
    }
    return () => clearInterval(timer);
  }, [loading]);

  const processDashboardData = (rows) => {
    let totalAmt = 0;
    
    // Gender Specific Accumulators (Fund amounts)
    let maleAmt = 0;
    let femAmt = 0;
    
    // Fallback counters (if IDs are missing)
    let maleCnt = 0;
    let femCnt = 0;

    // Start Year Calculation (Initialize with a high year)
    let minYearCalc = 9999;

    const uniqueStudents = new Set();
    const uniqueStreams = new Set();
    
    // Sets to track unique beneficiaries
    const maleStudentSet = new Set();
    const femaleStudentSet = new Set();

    const yearMap = {};     
    const streamMap = {};   
    
    // CHANGED: Use Sets for these maps to count Unique Students
    const genderMap = {};   
    const categoryMap = {}; 

    rows.forEach(r => {
      const amt = getSafeAmount(r);
      const sid = getSafeVal(r, "STUDENT_ID");
      const fy = getSafeVal(r, "FINANCE_YEAR", "Unknown");
      const gen = getSafeVal(r, "GENDER", "Other").toUpperCase();
      const strm = getSafeVal(r, "STREAM", "General");
      const cat = getSafeVal(r, "CATEGORY", "Other");

      // --- 1. Fetch Start Year Logic ---
      if (fy && fy.includes("-")) {
        const parts = fy.split("-"); 
        const firstPart = parseInt(parts[0]);
        if (!isNaN(firstPart) && firstPart < minYearCalc) {
            minYearCalc = firstPart;
        }
      }

      // General Aggregates
      totalAmt += amt;
      if (sid !== "UNKNOWN") uniqueStudents.add(sid);
      uniqueStreams.add(strm);

      // Gender Specific Stats & Totals
      if (gen === "MALE") {
        maleAmt += amt;
        if(sid !== "UNKNOWN") maleStudentSet.add(sid);
        else maleCnt++; 
      } else if (gen === "FEMALE") {
        femAmt += amt;
        if(sid !== "UNKNOWN") femaleStudentSet.add(sid);
        else femCnt++;
      }

      // Trend Data (Sum of Amount)
      if (!yearMap[fy]) yearMap[fy] = 0;
      yearMap[fy] += amt;

      // Stream Data (Sum of Amount)
      if (!streamMap[strm]) streamMap[strm] = 0;
      streamMap[strm] += amt;

      // --- FIX: Count Unique Students for Charts ---
      
      // Gender Pie Data
      if (!genderMap[gen]) genderMap[gen] = new Set();
      if (sid !== "UNKNOWN") genderMap[gen].add(sid);

      // Category Pie Data
      if (!categoryMap[cat]) categoryMap[cat] = new Set();
      if (sid !== "UNKNOWN") categoryMap[cat].add(sid);
    });

    // Data Transformations for Charts
    const trendArr = Object.keys(yearMap).sort().map(k => ({
      name: k,
      Amount: Math.round(yearMap[k]) 
    }));

    // FIX: Use .size to get unique count
    const genderArr = Object.keys(genderMap).map(k => ({
      name: k,
      value: genderMap[k].size 
    }));



 

    // Calculate Average
    const totalCount = uniqueStudents.size || 1; 
    const average = totalAmt / totalCount;

    // Update States
    if (minYearCalc !== 9999) {
        setStartYear(minYearCalc.toString());
    }

    setStats({
      totalBeneficiaries: uniqueStudents.size,
      totalDisbursed: totalAmt,
      avgDisbursal: average,
      totalStreams: uniqueStreams.size,
      maleBeneficiaries: maleStudentSet.size > 0 ? maleStudentSet.size : maleCnt, 
      femaleBeneficiaries: femaleStudentSet.size > 0 ? femaleStudentSet.size : femCnt,
      maleFund: maleAmt,
      femaleFund: femAmt
    });

    setTrendData(trendArr);
    setGenderData(genderArr);
 
  };

  const fetchDashboardData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetchJSON(`${API}/api/pms/filter`, {
        method: "POST",
        headers: { 
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}` 
        },
        body: JSON.stringify({ from_fy: "ALL", to_fy: "ALL", state: "ALL" }),
      });

      if (res.ok && res.data.rows) {
        processDashboardData(res.data.rows);
      } else {
        throw new Error("Failed to load data");
      }
    } catch (error) {
      console.error(error);
      const dummyRows = [
         { STUDENT_ID: "1", STUDENT_NAME: "Amit Kumar", AMT_PAID: "50000", FINANCE_YEAR: "2024-2025", MONTH_YEAR: "Dec-2024", GENDER: "MALE", STREAM: "ENGINEERING", CATEGORY: "SC" },
         { STUDENT_ID: "2", STUDENT_NAME: "Priya Singh", AMT_PAID: "45000", FINANCE_YEAR: "2024-2025", MONTH_YEAR: "Jan-2025", GENDER: "FEMALE", STREAM: "MEDICAL", CATEGORY: "OBC" },
         { STUDENT_ID: "3", STUDENT_NAME: "Rahul Ver", AMT_PAID: "25000", FINANCE_YEAR: "2024-2025", MONTH_YEAR: "Feb-2025", GENDER: "MALE", STREAM: "ARTS", CATEGORY: "ST" },
      ];
      processDashboardData(dummyRows);
    } finally {
      setTimeout(() => setLoading(false), 500);
    }
  }, [token]);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  if (loading) {
    return (
      <div style={{ padding: "40px", maxWidth: "600px", margin: "0 auto", textAlign: "center" }}>
        <h3 style={{ color: "#666", marginBottom: "20px" }}>Analyzing Financial Data... {loadingPercent}%</h3>
        <div style={{ width: "100%", height: "8px", background: "#e0e0e0", borderRadius: "4px", overflow: "hidden" }}>
          <div style={{ width: `${loadingPercent}%`, height: "100%", background: THEME_BLUE, transition: "width 0.2s ease" }} />
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-container" style={{ display: "flex", flexDirection: "column", gap: "25px", paddingBottom: "40px" }}>
      
      {/* 1. HEADER */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <h2 style={{ margin: 0, color: "#333", borderLeft: `6px solid ${THEME_BLUE}`, paddingLeft: "15px" }}>
          NFSC: PMS Analytics Dashboard
        </h2>
        <span style={{ fontSize: "0.9rem", color: "#4d4d4d", fontWeight: "500" }}>
           Overview of scholarship disbursement data since {startYear}
        </span>
      </div>

      {/* 2. KPI CARDS GRID */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: "20px" }}>
        {/* Row 1 */}
        <KpiCard title="Total Beneficiaries" value={stats.totalBeneficiaries.toLocaleString()} icon="groups" color="#2980b9" />
        <KpiCard title="Male Beneficiaries" value={stats.maleBeneficiaries.toLocaleString()} icon="male" color="#3498db" />
        <KpiCard title="Female Beneficiaries" value={stats.femaleBeneficiaries.toLocaleString()} icon="female" color="#9b59b6" />
        
        <KpiCard 
            title="Avg. Fund / Student" 
            value={formatCurrency(stats.avgDisbursal)} 
            icon="analytics" 
            color="#8e44ad" 
        />
        
        {/* Row 2 */}
        <KpiCard title="Total Fund Released" value={formatCurrency(stats.totalDisbursed)} icon="account_balance" color="#27ae60" />
        <KpiCard title="Fund Release to Male Beneficiaries" value={formatCurrency(stats.maleFund)} icon="payments" color="#1abc9c" />
        <KpiCard title="Fund Release to Female Beneficiaries" value={formatCurrency(stats.femaleFund)} icon="savings" color="#e91e63" />
        <KpiCard title="Active Streams" value={stats.totalStreams} icon="school" color="#f39c12" />
      </div>

      {/* 3. TREND & STREAM CHARTS */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(400px, 1fr))", gap: "25px" }}>
        
        {/* LEFT: Fund Release Trend */}
        <div className="card" style={{ padding: "20px", height: "400px", display: "flex", flexDirection: "column" }}>
          <h3 style={{ margin: "0 0 20px 0", color: "#444" }}>Fund Release Trend (Yearly)</h3>
          <div style={{ flex: 1 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={trendData} margin={{ top: 20, right: 30, left: 10, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="name" />
                <YAxis 
                  width={120} 
                  tickFormatter={(value) => value.toLocaleString("en-IN")} 
                />
                <Tooltip 
                  cursor={{ fill: '#f0f0f0' }}
                  formatter={(value) => formatCurrency(value)} 
                />
                <Legend />
                <Bar 
                  dataKey="Amount" 
                  name="Disbursed Amount" 
                  fill={THEME_BLUE} 
                  barSize={50} 
                  radius={[5, 5, 0, 0]} 
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
 {/* 4. PIE CHARTS */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(400px, 1fr))", gap: "25px" }}>
        <div className="card" style={{ padding: "20px", height: "350px", display: "flex", flexDirection: "column" }}>
            <h3 style={{ margin: "0 0 10px 0", color: "#444", textAlign: 'center' }}>Beneficiaries by Gender</h3>
            <div style={{ flex: 1 }}>
                <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                        <Pie data={genderData} cx="50%" cy="50%" innerRadius={60} outerRadius={80} paddingAngle={5} dataKey="value" label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}>
                            {genderData.map((entry, index) => <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />)}
                        </Pie>
                        <Tooltip />
                        <Legend />
                    </PieChart>
                </ResponsiveContainer>
            </div>
        </div>

       
      </div>
       
      </div>

     

    </div>
  );
}

// --- Helper Components ---
function KpiCard({ title, value, icon, color, subText }) {
  return (
    <div className="card" style={{ position: "relative", overflow: "hidden", padding: "20px", display: "flex", flexDirection: "column", justifyContent: "space-between", minHeight: "130px" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "start" }}>
        <div>
          <div style={{ fontSize: "0.8rem", color: "#888", fontWeight: "600", textTransform: "uppercase", letterSpacing: "0.5px" }}>{title}</div>
          <div style={{ fontSize: "1.6rem", fontWeight: "700", color: "#2c3e50", marginTop: "8px" }}>{value}</div>
        </div>
        <div style={{ background: `${color}15`, borderRadius: "10px", padding: "8px", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <span className="material-icons" style={{ color: color, fontSize: "24px" }}>{icon}</span>
        </div>
      </div>
    </div>
  );
}